/**
 * Enhanced Admin Users Management Page (PR A10)
 * URL state, ConfirmDialog, Toast, RowActions, Optimistic updates
 * Global Column Sorting via useTableSort hook
 * P1: Live search with debounce (no button click required)
 * P2: QC Product Scope management for qc_specialist role
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { usePermissions } from '../hooks/usePermissions';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { 
  listUsers, 
  resetUserPassword, 
  adminResetUserPassword,
  lockUser, 
  unlockUser, 
  startImpersonation,
  getUser,
  resendVerificationEmail,
  updateUserQCRequirement,
  updateUserQCProductScope
} from '../utils/usersApi';
import { getUserFromToken } from '../utils/adminApi';
import { getUserStatus } from '../utils/userStatus';
import UserDetailsDrawer from '../components/UserDetailsDrawer';
import UsersPagination from '../components/UsersPagination';
import RowActions from '../components/users/RowActions';
import UserFormDialog from '../components/users/UserFormDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { userActionToasts, toast } from '../utils/toast';
import useTableSort, { sortData } from '../hooks/useTableSort';
import { Button } from '../components/ui/button';
import { Plus, X, ChevronLeft, ChevronRight, Home, Search } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import './AdminUsersPage.css';
import '../components/hr/TeamManagementDark.css';
import './AdminUsersPageDark.css';
import './AdminUsersPremium.css';

// USERS-UI-002: light-shell palettes (badges + avatar gradients). Dark IT shell keeps ROLE_COLORS.
const ROLE_COLORS_LIGHT = {
  admin:                    { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca', dot: '#ef4444', av: ['#f87171', '#dc2626'] },
  order_desk:               { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6', av: ['#60a5fa', '#2563eb'] },
  processor:                { bg: '#d1fae5', color: '#047857', border: '#a7f3d0', dot: '#10b981', av: ['#34d399', '#059669'] },
  tax_processor:            { bg: '#d1fae5', color: '#047857', border: '#a7f3d0', dot: '#10b981', av: ['#34d399', '#059669'] },
  search_processor:         { bg: '#cffafe', color: '#0e7490', border: '#a5f3fc', dot: '#06b6d4', av: ['#22d3ee', '#0891b2'] },
  typing_processor:         { bg: '#e0e7ff', color: '#4338ca', border: '#c7d2fe', dot: '#6366f1', av: ['#818cf8', '#4f46e5'] },
  tax_search_team_leader:   { bg: '#fef3c7', color: '#b45309', border: '#fde68a', dot: '#f59e0b', av: ['#fbbf24', '#d97706'] },
  title_search_team_leader: { bg: '#ffedd5', color: '#c2410c', border: '#fed7aa', dot: '#f97316', av: ['#fb923c', '#ea580c'] },
  jurisdiction_manager:     { bg: '#ede9fe', color: '#6d28d9', border: '#ddd6fe', dot: '#8b5cf6', av: ['#a78bfa', '#7c3aed'] },
  jurisdiction_requestor:   { bg: '#e0e7ff', color: '#4338ca', border: '#c7d2fe', dot: '#6366f1', av: ['#818cf8', '#4f46e5'] },
  qc_specialist:            { bg: '#fce7f3', color: '#be185d', border: '#fbcfe8', dot: '#ec4899', av: ['#f472b6', '#db2777'] },
  sales_marketing:          { bg: '#ede9fe', color: '#5b21b6', border: '#ddd6fe', dot: '#8b5cf6', av: ['#a78bfa', '#6d28d9'] },
  it_support:               { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', dot: '#64748b', av: ['#94a3b8', '#475569'] },
  it_manager:               { bg: '#e2e8f0', color: '#334155', border: '#cbd5e1', dot: '#475569', av: ['#64748b', '#334155'] },
  hr:                       { bg: '#fce7f3', color: '#be185d', border: '#fbcfe8', dot: '#f472b6', av: ['#f9a8d4', '#db2777'] },
  hr_manager:               { bg: '#fae8ff', color: '#a21caf', border: '#f5d0fe', dot: '#d946ef', av: ['#e879f9', '#a21caf'] },
  accounting:               { bg: '#d1fae5', color: '#047857', border: '#a7f3d0', dot: '#34d399', av: ['#6ee7b7', '#059669'] },
};
const ROLE_FILTER_OPTIONS = [
  ['admin', 'Admin'], ['order_desk', 'Order Desk'], ['processor', 'Tax Processor'], ['search_processor', 'Search Processor'],
  ['typing_processor', 'Typing Processor'], ['tax_search_team_leader', 'Tax Search Team Leader'], ['title_search_team_leader', 'Title Search Team Leader'],
  ['qc_specialist', 'QC Specialist'], ['sales_marketing', 'Sales & Marketing'], ['jurisdiction_manager', 'Jurisdiction Manager'],
  ['jurisdiction_requestor', 'Jurisdiction Requestor'], ['it_support', 'IT Support'], ['it_manager', 'IT Manager'],
  ['hr', 'HR Specialist'], ['hr_manager', 'HR Manager'], ['accounting', 'Accounting'],
];
const STATUS_FILTER_OPTIONS = [
  ['active', 'Active', '#10b981'], ['locked', 'Locked', '#f59e0b'], ['suspended', 'Suspended', '#f97316'],
  ['disabled', 'Disabled', '#ef4444'], ['pending_activation', 'Pending Activation', '#64748b'], ['pending_verification', 'Pending Verification', '#8b5cf6'],
];
const userInitials = (u) => ((u.first_name && u.last_name) ? `${u.first_name[0]}${u.last_name[0]}` : (u.email || '?')[0]).toUpperCase();

const AdminUsersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalPages: 0,
    totalItems: 0
  });
  // Feb 28, 2026 — backend-driven filters for the Admin Users
  // Management redesign. Role / status are applied server-side so
  // every user is reachable across pages, not just the current view.
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { searchInput: searchQuery, setSearchInput: setSearchQuery, debouncedValue: debouncedSearchQuery, clearSearch: clearSearchState } = useDebouncedSearch('', 350);
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  // User form dialog state (PR A17)
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
    showInput: false,
    inputValue: ''
  });

  // P1: Admin Immediate Password Reset modal state
  // Two phases:
  //   1. "confirm"  → warning + reason input
  //   2. "result"   → shows temp password (one-time view, copy button)
  const [adminResetModal, setAdminResetModal] = useState({
    isOpen: false,
    phase: 'confirm',   // 'confirm' | 'result'
    user: null,
    reason: '',
    submitting: false,
    error: null,
    tempPassword: '',
    copied: false
  });
  
  // Get current user from token
  const currentUser = getUserFromToken();
  const currentUserId = currentUser?.id;
  const impersonationActive = currentUser?.isImpersonating;
  // USERS-SHELL-001: IT portal roles get the IT Operations Center dark skin.
  // PORTAL-CONTEXT-001: Admin browsing from the IT Operations Center too.
  let itPortalContext = false;
  try { itPortalContext = sessionStorage.getItem('titleflow-portal-context') === 'it'; } catch (_) { /* noop */ }
  const isITPortalUser = currentUser?.role === 'it_manager' || currentUser?.role === 'it_support'
    || (currentUser?.role === 'admin' && itPortalContext);

  // Check if user has view permission
  const canView = hasPermission('users', 'view');

  // Table sorting hook
  const {
    sortConfig,
    handleSort,
    getSortDirection
  } = useTableSort({
    defaultSortKey: 'last_login_at',
    defaultSortOrder: 'desc'
  });

  // Sort users for display.
  //
  // Feb 28, 2026 — Sort is now PRIMARILY backend-driven (passed via
  // `sort_by` / `sort_order` to /admin/users). We keep a client-side
  // pass as a thin tie-breaker for any columns the backend can't sort
  // efficiently (e.g. role names that live in a separate collection).
  // For the columns the backend DOES sort (name/email/status/dates),
  // the client pass is a no-op stable sort.
  const sortedUsers = useMemo(() => {
    if (!users.length) return users;
    return sortData(users, sortConfig.key, sortConfig.direction);
  }, [users, sortConfig]);


  // Handle URL userId and action parameters
  useEffect(() => {
    const userIdFromUrl = searchParams.get('userId');
    const actionFromUrl = searchParams.get('action');
    
    if (userIdFromUrl && !drawerOpen) {
      fetchUserAndOpenDrawer(userIdFromUrl, actionFromUrl);
    }
  }, [searchParams]);

  const fetchUserAndOpenDrawer = async (userId, action = null) => {
    try {
      const userData = await getUser(userId);
      setSelectedUser(userData);
      setDrawerOpen(true);
      
      // If action is specified, trigger it after drawer opens
      if (action) {
        setTimeout(() => {
          if (action === 'unlock' && (userData.status === 'locked' || userData.status === 'suspended')) {
            handleLockUnlock(userData);
          } else if (action === 'lock' && userData.status === 'active') {
            handleLockUnlock(userData);
          } else if (action === 'reset') {
            handleResetPassword(userData);
          } else if (action === 'impersonate') {
            handleImpersonate(userData);
          }
        }, 500);
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      toast.error('Failed to load user details');
    }
  };

  useEffect(() => {
    if (!permissionsLoading && canView) {
      fetchUsers();
    } else if (!permissionsLoading && !canView) {
      setLoading(false);
    }
    // Feb 28, 2026 — Include role/status filters and sort in the
    // refetch trigger so changing them re-runs the backend query.
    // `sortConfig.key` is mapped to the backend `sort_by` param.
  }, [pagination.page, pagination.pageSize, roleFilter, statusFilter, sortConfig.key, sortConfig.direction, permissionsLoading, canView]);

  const fetchUsers = async (overrideQuery = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use override query if provided, otherwise use state
      const queryToUse = overrideQuery !== null ? overrideQuery : searchQuery;
      
      const response = await listUsers({
        page: pagination.page,
        pageSize: pagination.pageSize,
        query: queryToUse,
        roleFilter,
        statusFilter,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
      });
      
      setUsers(response.items || response);
      
      // Update pagination if available
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items
        }));
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // P1: LIVE SEARCH WITH DEBOUNCE (via useDebouncedSearch hook)
  // =========================================
  
  // Trigger search when debounced value changes
  const prevDebouncedRef = useRef(debouncedSearchQuery);
  useEffect(() => {
    if (prevDebouncedRef.current === debouncedSearchQuery) return;
    prevDebouncedRef.current = debouncedSearchQuery;
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers(debouncedSearchQuery);
  }, [debouncedSearchQuery]);
  
  // Clear search and reload full list
  const handleClearSearch = useCallback(() => {
    clearSearchState();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers('');
  }, []);

  // Feb 28, 2026 — Filter / page-size handlers reset to page 1 so
  // the user doesn't land on an empty page after narrowing the set.
  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, pageSize: Number(newSize) || 25, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setDrawerOpen(true);
    // Update URL with userId
    const newParams = new URLSearchParams(searchParams);
    newParams.set('userId', user.id);
    setSearchParams(newParams);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedUser(null), 300);
    // Remove userId from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('userId');
    setSearchParams(newParams);
  };

  const handleResetPassword = (user) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reset password?',
      message: `This will send a password reset email to ${user.email}. The user will be required to set a new password using the link in the email.`,
      confirmText: 'Send Reset Email',
      confirmButtonClass: 'btn-confirm-warning',
      onConfirm: async () => {
        try {
          setActionLoading(`reset-${user.id}`);
          await resetUserPassword(user.id, 'Admin-initiated reset');
          
          // Show success confirmation dialog
          setConfirmDialog({
            isOpen: true,
            title: '✅ Password Reset Email Sent',
            message: `A password reset link has been sent to:\n\n${user.email}\n\nThe user will be required to set a new password using the link in the email. The link expires in 30 minutes.`,
            confirmText: 'OK',
            confirmButtonClass: 'btn-confirm-success',
            showCancelButton: false,
            onConfirm: () => {
              setConfirmDialog({ isOpen: false });
            }
          });
          
          await fetchUsers();
        } catch (err) {
          // Show error dialog
          let errorMessage = 'Failed to send password reset email. Please try again.';
          
          if (err.message.includes('429')) {
            errorMessage = 'Too many reset requests. Please wait a few minutes before trying again.';
          } else if (err.message.includes('403')) {
            errorMessage = 'You do not have permission to reset this user\'s password.';
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          setConfirmDialog({
            isOpen: true,
            title: '❌ Password Reset Failed',
            message: errorMessage,
            confirmText: 'OK',
            confirmButtonClass: 'btn-confirm-danger',
            showCancelButton: false,
            onConfirm: () => {
              setConfirmDialog({ isOpen: false });
            }
          });
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  // P1: Admin Immediate Reset — opens warning modal, then on confirm hits
  // the backend and transitions the modal into the result phase showing
  // the temp password ONCE with a copy-to-clipboard button.
  const handleAdminResetPassword = (user) => {
    setAdminResetModal({
      isOpen: true,
      phase: 'confirm',
      user,
      reason: '',
      submitting: false,
      error: null,
      tempPassword: '',
      copied: false
    });
  };

  const handleAdminResetSubmit = async () => {
    const { user, reason } = adminResetModal;
    const trimmedReason = (reason || '').trim();
    if (trimmedReason.length < 3) {
      setAdminResetModal(m => ({ ...m, error: 'Please provide a reason (min 3 characters).' }));
      return;
    }
    setAdminResetModal(m => ({ ...m, submitting: true, error: null }));
    try {
      const resp = await adminResetUserPassword(user.id, trimmedReason);
      setAdminResetModal(m => ({
        ...m,
        submitting: false,
        phase: 'result',
        tempPassword: resp.temporary_password,
      }));
      // Refresh the users list so password_reset_required / lockout cleared
      try { await fetchUsers(); } catch { /* non-blocking */ }
    } catch (err) {
      let msg = 'Failed to reset password. Please try again.';
      if (err.status === 403 || /permission|super admin/i.test(err.message || '')) {
        msg = 'Only Admins can perform immediate password resets.';
      } else if (err.status === 400 && /own account/i.test(err.message || '')) {
        msg = "You cannot use admin-reset on your own account.";
      } else if (err.message) {
        msg = err.message;
      }
      setAdminResetModal(m => ({ ...m, submitting: false, error: msg }));
    }
  };

  const handleAdminResetCopy = async () => {
    try {
      await navigator.clipboard.writeText(adminResetModal.tempPassword);
      setAdminResetModal(m => ({ ...m, copied: true }));
      setTimeout(() => setAdminResetModal(m => ({ ...m, copied: false })), 2000);
    } catch {
      /* ignore — user can still select + copy manually */
    }
  };

  const handleAdminResetClose = () => {
    setAdminResetModal({
      isOpen: false,
      phase: 'confirm',
      user: null,
      reason: '',
      submitting: false,
      error: null,
      tempPassword: '',
      copied: false
    });
  };

  // Handle resend verification email
  const handleResendVerification = (user) => {
    // Check if user is already verified
    if (user.email_verified === true && user.status === 'active') {
      setConfirmDialog({
        isOpen: true,
        title: 'ℹ️ User Already Verified',
        message: `${user.email} has already been verified and is active.`,
        confirmText: 'OK',
        confirmButtonClass: 'btn-confirm-info',
        showCancelButton: false,
        onConfirm: () => setConfirmDialog({ isOpen: false })
      });
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Send Verification Email?',
      message: `This will send an email verification link to ${user.email}. The user will need to click the link to verify their email and activate their account.`,
      confirmText: 'Send Verification Email',
      confirmButtonClass: 'btn-confirm-warning',
      onConfirm: async () => {
        try {
          setActionLoading(`verify-${user.id}`);
          await resendVerificationEmail(user.id);
          
          // Show success confirmation dialog
          setConfirmDialog({
            isOpen: true,
            title: '✅ Verification Email Sent',
            message: `A verification link has been sent to:\n\n${user.email}\n\nThe user will need to click the link to verify their email. The link expires in 24 hours.`,
            confirmText: 'OK',
            confirmButtonClass: 'btn-confirm-success',
            showCancelButton: false,
            onConfirm: () => {
              setConfirmDialog({ isOpen: false });
            }
          });
          
          await fetchUsers();
        } catch (err) {
          // Show error dialog
          let errorMessage = 'Failed to send verification email. Please try again.';
          
          if (err.message.includes('429')) {
            errorMessage = 'Too many requests. Please wait 5 minutes before sending another verification email.';
          } else if (err.message.includes('403')) {
            errorMessage = 'You do not have permission to send verification emails.';
          } else if (err.message.includes('400') && err.message.includes('already verified')) {
            errorMessage = 'This user\'s email is already verified.';
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          setConfirmDialog({
            isOpen: true,
            title: '❌ Verification Email Failed',
            message: errorMessage,
            confirmText: 'OK',
            confirmButtonClass: 'btn-confirm-danger',
            showCancelButton: false,
            onConfirm: () => {
              setConfirmDialog({ isOpen: false });
            }
          });
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const handleLockUnlock = (user) => {
    const { isLocked } = getUserStatus(user);
    
    setConfirmDialog({
      isOpen: true,
      title: isLocked ? 'Unlock account?' : 'Lock account?',
      message: isLocked 
        ? 'The user will be able to sign in again.'
        : 'The user will be prevented from signing in until unlocked.',
      confirmText: isLocked ? 'Unlock account' : 'Lock account',
      confirmButtonClass: isLocked ? 'btn-confirm' : 'btn-confirm-danger',
      onConfirm: async () => {
        // Optimistic update
        const previousStatus = user.status;
        const newStatus = isLocked ? 'active' : 'locked';
        
        setUsers(users.map(u => 
          u.id === user.id ? { ...u, status: newStatus } : u
        ));
        
        if (selectedUser?.id === user.id) {
          setSelectedUser({ ...selectedUser, status: newStatus });
        }

        try {
          setActionLoading(`lock-${user.id}`);
          
          if (isLocked) {
            await unlockUser(user.id);
            userActionToasts.unlockSuccess();
          } else {
            await lockUser(user.id, 'Admin-initiated lock');
            userActionToasts.lockSuccess();
          }
          
          await fetchUsers();
        } catch (err) {
          // Rollback on error
          setUsers(users.map(u => 
            u.id === user.id ? { ...u, status: previousStatus } : u
          ));
          
          if (selectedUser?.id === user.id) {
            setSelectedUser({ ...selectedUser, status: previousStatus });
          }
          
          if (err.message.includes('429')) {
            userActionToasts.rateLimitExceeded();
          } else if (err.message.includes('403')) {
            userActionToasts.permissionDenied();
          } else {
            userActionToasts.actionFailed(err.message);
          }
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const handleImpersonate = (user) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Start impersonation?',
      message: 'You\'ll act as this user for 30 minutes or until you stop. Reason is recorded in the audit log.',
      confirmText: 'Start impersonation',
      confirmButtonClass: 'btn-confirm-warning',
      showInput: true,
      inputPlaceholder: 'Reason (optional)',
      inputValue: '',
      onConfirm: async () => {
        try {
          setActionLoading(`impersonate-${user.id}`);
          const response = await startImpersonation(user.id, confirmDialog.inputValue || 'Admin investigation');
          
          localStorage.setItem('token', response.impersonation_token);
          userActionToasts.impersonateSuccess();
          
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } catch (err) {
          if (err.message.includes('403')) {
            userActionToasts.permissionDenied();
          } else {
            userActionToasts.actionFailed(err.message);
          }
          setActionLoading(null);
        }
      }
    });
  };

  // User CRUD handlers (PR A17)
  const handleCreateUser = () => {
    setEditingUser(null);
    setFormDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormDialogOpen(true);
  };

  const handleFormSuccess = () => {
    // Refresh users list
    fetchUsers();
  };

  // P0 QC Workflow: Loading state for QC toggle
  const [qcToggleLoading, setQcToggleLoading] = useState(null);

  // P0 QC Workflow: Toggle requires_qc flag for a user (Slice 5)
  const handleToggleQCRequirement = async (user, e) => {
    e.stopPropagation(); // Prevent row click
    
    const newValue = !user.requires_qc;
    const userName = user.first_name && user.last_name 
      ? `${user.first_name} ${user.last_name}` 
      : user.email;
    
    // Set loading state
    setQcToggleLoading(user.id);
    
    // Optimistic update
    setUsers(prevUsers => 
      prevUsers.map(u => 
        u.id === user.id ? { ...u, requires_qc: newValue } : u
      )
    );
    
    try {
      await updateUserQCRequirement(user.id, newValue);
      toast.success(
        newValue 
          ? `✓ QC review enabled for ${userName}` 
          : `✗ QC review disabled for ${userName} - orders will complete directly`
      );
    } catch (error) {
      // Revert on failure
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === user.id ? { ...u, requires_qc: !newValue } : u
        )
      );
      toast.error(`Failed to update QC requirement: ${error.message || 'Unknown error'}`);
    } finally {
      setQcToggleLoading(null);
    }
  };

  // Check if user role is processor (requires_qc only applies to processors)
  const isProcessorRole = (role) => {
    if (!role) return false;
    const normalizedRole = role.toLowerCase().trim();
    
    // Match both code-style and display-style role names
    const processorRoles = [
      'processor', 
      'tax_search_processor', 
      'title_search_processor',
      'search_processor',
      // Production display names
      'tax processor',
      'search processor',
      'title processor'
    ];
    
    // Direct match
    if (processorRoles.includes(normalizedRole)) return true;
    
    // Partial match for any role containing 'processor'
    return normalizedRole.includes('processor');
  };

  // P2 QC Product Scope: Check if user is QC Specialist
  const isQCSpecialist = (role) => {
    if (!role) return false;
    const normalizedRole = role.toLowerCase().trim();
    return normalizedRole === 'qc_specialist' || normalizedRole === 'qc specialist';
  };

  // P2 QC Product Scope: Modal state
  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  const [scopeEditUser, setScopeEditUser] = useState(null);
  const [scopeSelection, setScopeSelection] = useState({ tax: false, title: false });
  const [scopeSaving, setScopeSaving] = useState(false);

  // P2 QC Product Scope: Open scope editor modal
  const handleOpenScopeModal = (user, e) => {
    e.stopPropagation();
    const currentScope = user.qc_product_scope || [];
    setScopeSelection({
      tax: currentScope.includes('tax'),
      title: currentScope.includes('title')
    });
    setScopeEditUser(user);
    setScopeModalOpen(true);
  };

  // P2 QC Product Scope: Save scope changes
  const handleSaveScope = async () => {
    if (!scopeEditUser) return;
    
    const newScope = [];
    if (scopeSelection.tax) newScope.push('tax');
    if (scopeSelection.title) newScope.push('title');
    
    // Validate at least one scope selected
    if (newScope.length === 0) {
      toast.error('At least one product scope must be selected');
      return;
    }
    
    setScopeSaving(true);
    const userName = scopeEditUser.first_name && scopeEditUser.last_name 
      ? `${scopeEditUser.first_name} ${scopeEditUser.last_name}` 
      : scopeEditUser.email;
    
    try {
      const saved = await updateUserQCProductScope(scopeEditUser.id, newScope);
      // QC-SCOPE-DISPLAY-001: patch local state from the SERVER-confirmed scope (the
      // /admin/users list now returns qc_product_scope, so later refetches agree).
      const confirmedScope = Array.isArray(saved?.qc_product_scope) ? saved.qc_product_scope : newScope;
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === scopeEditUser.id ? { ...u, qc_product_scope: confirmedScope, qc_scope_updated_at: new Date().toISOString() } : u
        )
      );
      
      toast.success(`✓ QC scope updated for ${userName}: ${confirmedScope.join(', ')}`);
      setScopeModalOpen(false);
      setScopeEditUser(null);
    } catch (error) {
      toast.error(`Failed to update QC scope: ${error.message || 'Unknown error'}`);
    } finally {
      setScopeSaving(false);
    }
  };

  // P2 QC Product Scope: Render scope chips
  const renderScopeChips = (user) => {
    const scope = user.qc_product_scope || [];
    if (scope.length === 0) {
      return (
        <span 
          className="qc-scope-chip qc-scope-missing" 
          title="No scope configured - user cannot access QC queue"
          data-testid={`scope-missing-${user.id}`}
        >
          ⚠ Not Configured
        </span>
      );
    }
    return (
      <div className="qc-scope-chips" data-testid={`scope-chips-${user.id}`}
        title={user.qc_scope_updated_at ? `Scope last updated ${new Date(user.qc_scope_updated_at).toLocaleString()}` : undefined}>
        {scope.includes('tax') && (
          <span className="qc-scope-chip qc-scope-tax">Tax</span>
        )}
        {scope.includes('title') && (
          <span className="qc-scope-chip qc-scope-title">Title</span>
        )}
      </div>
    );
  };

  // Ensure naive datetime strings are treated as UTC (backend stores UTC without 'Z')
  const ensureUtc = (s) => {
    if (!s) return null;
    const str = String(s);
    if (str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str)) return str;
    return str + 'Z';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      // Compact admin-table format (May 8, 2026 fix — TF User Management
      // table layout overflow). Was 'MMM d, yyyy h:mm a' which produced
      // "May 7, 2026 1:29 PM" — long enough to wrap each token onto its
      // own line in the Last Login column. The full ISO timestamp is
      // already exposed via the cell's `title` attribute for hover
      // precision; the visible label can drop the year and use a bullet
      // separator. Format: "May 7 • 1:29 PM ET".
      return formatInTimeZone(ensureUtc(dateString), 'America/New_York', 'MMM d • h:mm a') + ' ET';
    } catch (error) {
      return dateString;
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return null;
    try {
      const now = new Date();
      const date = new Date(ensureUtc(dateString));
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
      return `${Math.floor(diffDays / 30)}mo ago`;
    } catch {
      return null;
    }
  };

  const ROLE_COLORS = {
    admin:                    { bg: 'rgba(239, 68, 68, 0.12)',  color: '#f87171',  border: 'rgba(239, 68, 68, 0.25)' },
    order_desk:               { bg: 'rgba(59, 130, 246, 0.12)', color: '#60a5fa',  border: 'rgba(59, 130, 246, 0.25)' },
    processor:                { bg: 'rgba(16, 185, 129, 0.12)', color: '#34d399',  border: 'rgba(16, 185, 129, 0.25)' },
    tax_processor:            { bg: 'rgba(16, 185, 129, 0.12)', color: '#34d399',  border: 'rgba(16, 185, 129, 0.25)' },
    search_processor:         { bg: 'rgba(6, 182, 212, 0.12)',  color: '#22d3ee',  border: 'rgba(6, 182, 212, 0.25)' },
    tax_search_team_leader:   { bg: 'rgba(245, 158, 11, 0.12)', color: '#fbbf24',  border: 'rgba(245, 158, 11, 0.25)' },
    title_search_team_leader: { bg: 'rgba(249, 115, 22, 0.12)', color: '#fb923c',  border: 'rgba(249, 115, 22, 0.25)' },
    jurisdiction_manager:     { bg: 'rgba(139, 92, 246, 0.12)', color: '#a78bfa',  border: 'rgba(139, 92, 246, 0.25)' },
    jurisdiction_requestor:   { bg: 'rgba(99, 102, 241, 0.12)', color: '#818cf8',  border: 'rgba(99, 102, 241, 0.25)' },
    qc_specialist:            { bg: 'rgba(236, 72, 153, 0.12)', color: '#f472b6',  border: 'rgba(236, 72, 153, 0.25)' },
    sales_marketing:          { bg: 'rgba(109, 78, 224, 0.12)',  color: '#a78bfa',  border: 'rgba(109, 78, 224, 0.25)' },
    it_support:               { bg: 'rgba(100, 116, 139, 0.12)',color: '#94a3b8',  border: 'rgba(100, 116, 139, 0.25)' },
    it_manager:               { bg: 'rgba(71, 85, 105, 0.15)',  color: '#cbd5e1',  border: 'rgba(71, 85, 105, 0.3)' },
    hr:                       { bg: 'rgba(244, 114, 182, 0.12)',color: '#f9a8d4',  border: 'rgba(244, 114, 182, 0.25)' },
    hr_manager:               { bg: 'rgba(232, 121, 249, 0.12)',color: '#e879f9',  border: 'rgba(232, 121, 249, 0.25)' },
    accounting:               { bg: 'rgba(52, 211, 153, 0.12)', color: '#6ee7b7',  border: 'rgba(52, 211, 153, 0.25)' },
  };

  const getRoleBadgeStyle = (roleKey) => {
    const key = (roleKey || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    if (!isITPortalUser) return ROLE_COLORS_LIGHT[key] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', dot: '#94a3b8', av: ['#94a3b8', '#64748b'] };
    return ROLE_COLORS[key] || { bg: 'rgba(100, 116, 139, 0.1)', color: '#94a3b8', border: 'rgba(100, 116, 139, 0.2)' };
  };

  if (permissionsLoading) {
    return <div className="loading-container">Loading permissions...</div>;
  }

  if (!canView) {
    return (
      <div className="no-permission">
        <h2>Permission Denied</h2>
        <p>You don't have permission to view users.</p>
      </div>
    );
  }

  return (
    <div className={`admin-users-page${isITPortalUser ? ' tm-dark' : ' au-premium'}`}>
      {/* Breadcrumb Navigation — PORTAL-CONTEXT-001: stay inside the IT shell when in IT context */}
      {isITPortalUser ? (
        <div className="breadcrumb">
          <button className="breadcrumb-link" onClick={() => navigate('/admin/it-dashboard')}>← Dashboard</button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">User Management</span>
        </div>
      ) : (
        <nav className="au-crumbs" aria-label="Breadcrumb" data-testid="users-breadcrumb">
          <button type="button" onClick={() => navigate('/dashboard')} data-testid="breadcrumb-administration"><Home className="w-3.5 h-3.5" />Administration</button>
          <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
          <span className="au-crumb-current">Users</span>
        </nav>
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>{isITPortalUser ? 'User Management' : 'Users'}</h1>
          <p className="page-description">Manage user accounts, roles, and permissions</p>
        </div>
        {hasPermission('users', 'create') && (
          <Button onClick={handleCreateUser} className={isITPortalUser ? '' : 'au-primary-btn'} data-testid="create-user-btn">
            <Plus className="w-4 h-4" />
            Create User
          </Button>
        )}
      </div>

      {/* Search Bar — clean, minimal SaaS-style input */}
      <div className="au-toolbar-row">
      <div className={isITPortalUser ? 'mb-4' : 'au-toolbar'} data-testid="user-search-wrapper">
        <div className={isITPortalUser ? 'relative max-w-md' : 'relative w-full max-w-md au-toolbar-search'}>
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
            <Search className="w-4 h-4" />
          </span>
          <input
            type="text"
            className="au-search block w-full h-10 pl-9 pr-9 text-sm bg-white border border-slate-200 rounded-[10px] shadow-sm placeholder:text-slate-400 text-slate-900 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-colors"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="user-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 transition-colors"
              onClick={handleClearSearch}
              title="Clear search"
              data-testid="clear-search-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>
        {loading && searchQuery && (
          <span className="text-xs text-slate-500 mt-1.5 inline-block" data-testid="user-search-loading">
            Searching…
          </span>
        )}
      </div>

      {/*
        Filter row — Role + Status filters apply server-side so they
        narrow the FULL user set, not just the current page. Added
        Feb 28, 2026 as part of the Admin Users Management redesign.
      */}
      <div className={isITPortalUser ? 'flex flex-wrap items-center gap-3 mb-4' : 'au-toolbar-filters flex flex-wrap items-center gap-3'} data-testid="user-filter-row">
        {isITPortalUser ? (
          <>
            <div className="flex items-center gap-2">
              <label htmlFor="user-role-filter" className="text-xs uppercase tracking-wide text-slate-500 font-medium">Role</label>
              <select id="user-role-filter" data-testid="user-role-filter"
                className="h-9 px-2.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                value={roleFilter} onChange={(e) => handleRoleFilterChange(e.target.value)}>
                <option value="">All roles</option>
                {ROLE_FILTER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="user-status-filter" className="text-xs uppercase tracking-wide text-slate-500 font-medium">Status</label>
              <select id="user-status-filter" data-testid="user-status-filter"
                className="h-9 px-2.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)}>
                <option value="">All statuses</option>
                {STATUS_FILTER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="au-filter-label" id="user-role-filter-label">Role</span>
              <Select value={roleFilter || 'all'} onValueChange={(v) => handleRoleFilterChange(v === 'all' ? '' : v)}>
                <SelectTrigger className="au-select-trigger" aria-labelledby="user-role-filter-label" data-testid="user-role-filter" data-value={roleFilter || ''}>
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent className="au-select-content" data-testid="user-role-filter-menu">
                  <SelectItem value="all" data-testid="user-role-option-all">All roles</SelectItem>
                  {ROLE_FILTER_OPTIONS.map(([v, l]) => (
                    <SelectItem key={v} value={v} data-testid={`user-role-option-${v}`}>
                      <span className="au-role-dot" style={{ background: getRoleBadgeStyle(v).dot }} />{l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="au-filter-label" id="user-status-filter-label">Status</span>
              <Select value={statusFilter || 'all'} onValueChange={(v) => handleStatusFilterChange(v === 'all' ? '' : v)}>
                <SelectTrigger className="au-select-trigger" aria-labelledby="user-status-filter-label" data-testid="user-status-filter" data-value={statusFilter || ''}>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent className="au-select-content" data-testid="user-status-filter-menu">
                  <SelectItem value="all" data-testid="user-status-option-all">All statuses</SelectItem>
                  {STATUS_FILTER_OPTIONS.map(([v, l, dot]) => (
                    <SelectItem key={v} value={v} data-testid={`user-status-option-${v}`}>
                      <span className="au-role-dot" style={{ background: dot }} />{l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {(roleFilter || statusFilter) && (
          <button
            type="button"
            className={isITPortalUser ? 'h-9 px-3 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-md hover:bg-slate-50' : 'au-clear-btn'}
            onClick={() => { handleRoleFilterChange(''); handleStatusFilterChange(''); }}
            data-testid="clear-filters-btn"
          >
            Clear filters
          </button>
        )}
        {isITPortalUser && (
          <div className="ml-auto flex items-center gap-2">
            <label htmlFor="user-page-size" className="text-xs uppercase tracking-wide text-slate-500 font-medium">Rows</label>
            <select id="user-page-size" data-testid="user-page-size"
              className="h-9 px-2.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              value={pagination.pageSize} onChange={(e) => handlePageSizeChange(e.target.value)}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        )}
      </div>

      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Users Table */}
      {loading && users.length === 0 ? (
        isITPortalUser ? <div className="loading-container">Loading users...</div> : (
          <div className="table-container" data-testid="users-table-skeleton" aria-busy="true">
            <table className="users-table">
              <thead><tr>{['Name', 'Email', 'Role(s)', 'Status', 'QC Required', 'QC Scope', 'Last Login (ET)', 'Actions'].map((h) => <th key={h}><div className="th-content"><span>{h}</span></div></th>)}</tr></thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    <td><div className="au-user-cell"><span className="au-skel round" /><span className="au-skel" style={{ width: 120 }} /></div></td>
                    <td><span className="au-skel" style={{ width: 160 }} /></td>
                    <td><span className="au-skel" style={{ width: 96, height: 22, borderRadius: 8 }} /></td>
                    <td><span className="au-skel" style={{ width: 60, height: 22, borderRadius: 8 }} /></td>
                    <td><span className="au-skel" style={{ width: 40 }} /></td>
                    <td><span className="au-skel" style={{ width: 40 }} /></td>
                    <td><span className="au-skel" style={{ width: 90 }} /></td>
                    <td><span className="au-skel" style={{ width: 120 }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <>
          <div className="table-container" data-refreshing={loading ? 'true' : 'false'} aria-busy={loading || undefined}>
            <table className="users-table sortable-table">
              <thead>
                <tr>
                  <th 
                    className={`sortable-header ${getSortDirection('name') ? 'sorted' : ''}`}
                    onClick={() => handleSort('name')}
                  >
                    <div className="th-content">
                      <span>Name</span>
                      {getSortDirection('name') && (
                        <span className="sort-indicator">{getSortDirection('name') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('email') ? 'sorted' : ''}`}
                    onClick={() => handleSort('email')}
                  >
                    <div className="th-content">
                      <span>Email</span>
                      {getSortDirection('email') && (
                        <span className="sort-indicator">{getSortDirection('email') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('roles') ? 'sorted' : ''}`}
                    onClick={() => handleSort('roles')}
                  >
                    <div className="th-content">
                      <span>Role(s)</span>
                      {getSortDirection('roles') && (
                        <span className="sort-indicator">{getSortDirection('roles') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('status') ? 'sorted' : ''}`}
                    onClick={() => handleSort('status')}
                  >
                    <div className="th-content">
                      <span>Status</span>
                      {getSortDirection('status') && (
                        <span className="sort-indicator">{getSortDirection('status') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('requires_qc') ? 'sorted' : ''}`}
                    onClick={() => handleSort('requires_qc')}
                    title="QC Review Required - When enabled, completed orders go to QC review before final delivery"
                  >
                    <div className="th-content">
                      <span>QC Required</span>
                      {getSortDirection('requires_qc') && (
                        <span className="sort-indicator">{getSortDirection('requires_qc') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    title="P2: QC Product Scope - Which products a QC Specialist can review"
                  >
                    <div className="th-content">
                      <span>QC Scope</span>
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('last_login_at') ? 'sorted' : ''}`}
                    onClick={() => handleSort('last_login_at')}
                  >
                    <div className="th-content">
                      <span>Last Login (ET)</span>
                      {getSortDirection('last_login_at') && (
                        <span className="sort-indicator">{getSortDirection('last_login_at') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => {
                  const roleName = user.roles?.[0] || 'No role';
                  const roleKey = user.role || roleName;
                  const badgeStyle = getRoleBadgeStyle(roleKey);
                  const relative = formatRelativeTime(user.last_login_at);
                  const fullDate = formatDate(user.last_login_at);

                  const isSelected = drawerOpen && selectedUser?.id === user.id;
                  const hasName = !!(user.first_name && user.last_name);

                  return (
                  <tr key={user.id} onClick={() => handleRowClick(user)} onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(user); }} tabIndex={0}
                    className={`clickable-row${isSelected ? ' is-selected' : ''}`} aria-selected={isSelected || undefined} data-selected={isSelected ? 'true' : undefined}
                    data-testid={`user-row-${user.id}`}>
                    <td>
                      {isITPortalUser ? (
                        <div className="um-user-name">{hasName ? `${user.first_name} ${user.last_name}` : '-'}</div>
                      ) : (
                        <div className="au-user-cell">
                          <span className="au-avatar" style={{ '--av-a': badgeStyle.av?.[0], '--av-b': badgeStyle.av?.[1] }} aria-hidden="true" data-testid={`user-avatar-${user.id}`}>{userInitials(user)}</span>
                          <div className={`um-user-name${hasName ? '' : ' au-unnamed'}`}>{hasName ? `${user.first_name} ${user.last_name}` : '—'}</div>
                        </div>
                      )}
                    </td>
                    <td className="um-email-cell">
                      <span className="um-email" title={user.email}>{user.email}</span>
                    </td>
                    <td>
                      <span
                        className="um-role-badge"
                        style={{
                          background: badgeStyle.bg,
                          color: badgeStyle.color,
                          border: `1px solid ${badgeStyle.border}`,
                        }}
                        data-testid={`role-badge-${user.id}`}
                        title="Primary role"
                      >
                        {roleName}
                      </span>
                      {/* USER-DUAL-ROLE-UI-001: additional operational roles held besides the primary */}
                      {(user.roles || []).slice(1).map((extra) => (
                        <span key={extra} className="um-role-badge" title="Additional role"
                          style={{ marginLeft: 6, background: '#f1f5f9', color: '#334155', border: '1px dashed #94a3b8' }}
                          data-testid={`role-extra-${user.id}-${String(extra).toLowerCase().replace(/\s+/g, '-')}`}>
                          + {extra}
                        </span>
                      ))}
                      {/* ROLE-COMPAT-001: held roles break the compatibility matrix — flagged, never auto-modified */}
                      {Array.isArray(user.role_compat_issues) && user.role_compat_issues.length > 0 && (
                        <span className="um-role-badge" title={user.role_compat_issues.join(' ')}
                          style={{ marginLeft: 6, background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d' }}
                          data-testid={`role-compat-flag-${user.id}`}>
                          ⚠ role mix
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="status-cell">
                        {(() => {
                          const s = getUserStatus(user);
                          return (
                            <span
                              className={`um-status-badge ${s.badgeClass}`}
                              data-testid={`status-${user.id}`}
                            >
                              {s.label}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td>
                      {/* P0 QC Workflow: QC Required Toggle (Slice 5) - N/A for QC Specialists */}
                      {(user.roles || [user.role]).some(isQCSpecialist) ? (
                        <span className="qc-na" title="Not applicable for QC Specialists">
                          N/A
                        </span>
                      ) : isProcessorRole(user.roles?.[0] || user.role) ? (
                        <button
                          className={`um-qc-toggle ${user.requires_qc !== false ? 'um-qc-on' : 'um-qc-off'} ${qcToggleLoading === user.id ? 'qc-loading' : ''}`}
                          onClick={(e) => handleToggleQCRequirement(user, e)}
                          disabled={qcToggleLoading === user.id}
                          title="Click to toggle QC requirement"
                          data-testid={`qc-toggle-${user.id}`}
                        >
                          {qcToggleLoading === user.id ? (
                            <span className="qc-spinner">...</span>
                          ) : (
                            <>
                              <span className="um-qc-dot" />
                              <span>{user.requires_qc !== false ? 'Yes' : 'No'}</span>
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="qc-na">—</span>
                      )}
                    </td>
                    <td>
                      {/* P2 QC Product Scope: Only for QC Specialists */}
                      {(user.roles || [user.role]).some(isQCSpecialist) ? (
                        <div className="qc-scope-cell">
                          {renderScopeChips(user)}
                          <button
                            className="qc-scope-edit-btn"
                            onClick={(e) => handleOpenScopeModal(user, e)}
                            title="Edit QC product scope"
                            data-testid={`scope-edit-${user.id}`}
                          >
                            ✏️
                          </button>
                        </div>
                      ) : (
                        <span className="qc-na">—</span>
                      )}
                    </td>
                    <td>
                      <div className="um-login-time" title={fullDate} data-testid={`last-login-${user.id}`}>
                        {relative ? (
                          <>
                            <span className="um-login-relative">{relative}</span>
                            <span className="um-login-full">{fullDate}</span>
                          </>
                        ) : (
                          <span className="um-login-never">Never</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <RowActions
                        user={user}
                        onEdit={handleEditUser}
                        onImpersonate={handleImpersonate}
                        onLockUnlock={handleLockUnlock}
                        onResetPassword={handleResetPassword}
                        onAdminResetPassword={handleAdminResetPassword}
                        loading={actionLoading}
                        currentUserId={currentUserId}
                      />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {sortedUsers.length === 0 && (
              <div className="empty-state">
                <p>No users found</p>
              </div>
            )}
          </div>

          {/*
            Pagination — Industry-standard control with First/Prev/
            pages/Next/Last + "Showing X–Y of Z" summary. Always
            rendered when total_items > 0 (even for single page) so
            admins see the total user count without clicking around.
            Page size selector lives in the filter row above; this
            block only handles navigation.
          */}
          <UsersPagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            onPageChange={handlePageChange}
            loading={loading}
            pageSizeOptions={isITPortalUser ? null : [25, 50, 100]}
            onPageSizeChange={isITPortalUser ? null : handlePageSizeChange}
          />
        </>
      )}

      {/* User Details Drawer */}
      <UserDetailsDrawer
        user={selectedUser}
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        onResetPassword={handleResetPassword}
        onResendVerification={handleResendVerification}
        onLockUnlock={handleLockUnlock}
        onImpersonate={handleImpersonate}
        onEdit={hasPermission('users', 'update') ? handleEditUser : null}
        onUserUpdated={(userId, updates) => {
          // Sync table state when user is updated in drawer
          setUsers(prevUsers => 
            prevUsers.map(u => u.id === userId ? { ...u, ...updates } : u)
          );
          // Also update selected user if it's the same
          if (selectedUser?.id === userId) {
            setSelectedUser(prev => ({ ...prev, ...updates }));
          }
        }}
        currentUserId={currentUserId}
        impersonationActive={impersonationActive}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmButtonClass={confirmDialog.confirmButtonClass}
        showInput={confirmDialog.showInput}
        inputPlaceholder={confirmDialog.inputPlaceholder}
        inputValue={confirmDialog.inputValue}
        onInputChange={(val) => setConfirmDialog({ ...confirmDialog, inputValue: val })}
      />

      {/* User Form Dialog (PR A17) */}
      <UserFormDialog
        isOpen={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        user={editingUser}
        onSuccess={handleFormSuccess}
      />

      {/* P1: Admin Immediate Password Reset — two-phase modal
          Phase 1 (confirm): warning + reason required
          Phase 2 (result):  one-time temp password display + copy */}
      {adminResetModal.isOpen && (
        <div
          className="modal-overlay"
          onClick={!adminResetModal.submitting ? handleAdminResetClose : undefined}
          data-testid="admin-reset-modal-overlay"
        >
          <div
            className="admin-reset-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-reset-title"
            data-testid="admin-reset-modal"
          >
            {adminResetModal.phase === 'confirm' && (
              <>
                <div className="admin-reset-header admin-reset-header-warning">
                  <span className="admin-reset-icon" aria-hidden="true">⚡</span>
                  <h3 id="admin-reset-title" data-testid="admin-reset-title">
                    Admin Immediate Password Reset
                  </h3>
                </div>
                <div className="admin-reset-body">
                  <div className="admin-reset-warning" data-testid="admin-reset-warning">
                    <strong>Use only when the user is locked out and email reset isn't an option.</strong>
                    <ul>
                      <li>A new temporary password will be generated and shown <strong>once</strong>.</li>
                      <li>The user's current password will be <strong>invalidated immediately</strong>.</li>
                      <li>Any lockout and failed-attempt counters will be cleared.</li>
                      <li>The user will be forced to change the password on next login.</li>
                      <li>This action is recorded in the audit log.</li>
                    </ul>
                  </div>
                  <div className="admin-reset-target" data-testid="admin-reset-target">
                    <div className="admin-reset-target-label">Target user</div>
                    <div className="admin-reset-target-email">
                      {adminResetModal.user?.email}
                    </div>
                    <div className="admin-reset-target-meta">
                      {adminResetModal.user?.full_name ||
                        `${adminResetModal.user?.first_name || ''} ${adminResetModal.user?.last_name || ''}`.trim() ||
                        '—'}
                    </div>
                  </div>
                  <label className="admin-reset-reason-label" htmlFor="admin-reset-reason">
                    Reason <span className="admin-reset-required">*</span>
                  </label>
                  <textarea
                    id="admin-reset-reason"
                    className="admin-reset-reason"
                    rows={3}
                    placeholder="e.g. User locked out of production, email not receiving reset link"
                    value={adminResetModal.reason}
                    onChange={(e) => setAdminResetModal(m => ({
                      ...m, reason: e.target.value, error: null
                    }))}
                    disabled={adminResetModal.submitting}
                    data-testid="admin-reset-reason-input"
                    autoFocus
                  />
                  {adminResetModal.error && (
                    <div className="admin-reset-error" data-testid="admin-reset-error">
                      {adminResetModal.error}
                    </div>
                  )}
                </div>
                <div className="admin-reset-footer">
                  <Button
                    variant="outline"
                    onClick={handleAdminResetClose}
                    disabled={adminResetModal.submitting}
                    data-testid="admin-reset-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAdminResetSubmit}
                    disabled={adminResetModal.submitting ||
                              (adminResetModal.reason || '').trim().length < 3}
                    className="admin-reset-confirm-btn"
                    data-testid="admin-reset-confirm-btn"
                  >
                    {adminResetModal.submitting
                      ? 'Generating…'
                      : 'Generate Temporary Password'}
                  </Button>
                </div>
              </>
            )}

            {adminResetModal.phase === 'result' && (
              <>
                <div className="admin-reset-header admin-reset-header-success">
                  <span className="admin-reset-icon" aria-hidden="true">🔐</span>
                  <h3 id="admin-reset-title" data-testid="admin-reset-result-title">
                    Temporary Password Generated
                  </h3>
                </div>
                <div className="admin-reset-body">
                  <p className="admin-reset-one-time">
                    Copy this temporary password now — it will <strong>not be shown again</strong>.
                    Share it with <strong>{adminResetModal.user?.email}</strong> through a trusted
                    channel. The user will be required to change it on first login.
                  </p>
                  <div className="admin-reset-temp-box" data-testid="admin-reset-temp-box">
                    <code
                      className="admin-reset-temp-value"
                      data-testid="admin-reset-temp-password"
                    >
                      {adminResetModal.tempPassword}
                    </code>
                    <Button
                      size="sm"
                      onClick={handleAdminResetCopy}
                      className="admin-reset-copy-btn"
                      data-testid="admin-reset-copy-btn"
                    >
                      {adminResetModal.copied ? '✓ Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
                <div className="admin-reset-footer">
                  <Button
                    onClick={handleAdminResetClose}
                    data-testid="admin-reset-done-btn"
                  >
                    Done
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* P2 QC Product Scope Modal */}
      {scopeModalOpen && (
        <div className="modal-overlay" onClick={() => setScopeModalOpen(false)}>
          <div className="qc-scope-modal" onClick={(e) => e.stopPropagation()} data-testid="qc-scope-modal">
            <div className="modal-header">
              <h3>Edit QC Product Scope</h3>
              <Button 
                variant="ghost"
                size="icon"
                onClick={() => setScopeModalOpen(false)}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="modal-body">
              <p className="scope-user-info">
                Setting scope for: <strong>{scopeEditUser?.first_name} {scopeEditUser?.last_name || scopeEditUser?.email}</strong>
              </p>
              <p className="scope-description">
                Select which product types this QC Specialist can review. At least one must be selected.
              </p>
              {/* ROLE-COMPAT-001: scope must keep covering the processing roles this user holds */}
              {(() => {
                const held = (scopeEditUser?.role_ids || []).slice(1);
                const needs = [];
                if (held.some((r) => ['processor', 'tax_search_processor'].includes(r))) needs.push(['tax', 'Tax Processor']);
                if (held.some((r) => ['search_processor', 'typing_processor', 'title_search_processor'].includes(r))) needs.push(['title', 'Search / Typing Processor']);
                const primaryWs = ['processor'].includes(scopeEditUser?.role) ? ['tax', 'Tax Processor'] : ['search_processor', 'typing_processor', 'title_search_processor'].includes(scopeEditUser?.role) ? ['title', 'Search / Typing Processor'] : null;
                if (primaryWs && !needs.find((n) => n[0] === primaryWs[0])) needs.push(primaryWs);
                return needs.length ? (
                  <p className="scope-description" data-testid="scope-held-roles-hint" style={{ color: '#b45309' }}>
                    ⚠ This user also holds {needs.map((n) => n[1]).join(' and ')} — {needs.map((n) => n[0] === 'tax' ? 'Tax' : 'Title').join(' and ')} scope cannot be removed until that additional role is removed in Edit User.
                  </p>
                ) : null;
              })()}
              <div className="scope-checkboxes">
                <label className={`scope-checkbox ${scopeSelection.tax ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={scopeSelection.tax}
                    onChange={(e) => setScopeSelection(prev => ({ ...prev, tax: e.target.checked }))}
                    data-testid="scope-checkbox-tax"
                  />
                  <span className="checkbox-label">
                    <span className="checkbox-icon">{scopeSelection.tax ? '☑' : '☐'}</span>
                    Tax Orders
                  </span>
                </label>
                <label className={`scope-checkbox ${scopeSelection.title ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={scopeSelection.title}
                    onChange={(e) => setScopeSelection(prev => ({ ...prev, title: e.target.checked }))}
                    data-testid="scope-checkbox-title"
                  />
                  <span className="checkbox-label">
                    <span className="checkbox-icon">{scopeSelection.title ? '☑' : '☐'}</span>
                    Title Orders
                  </span>
                </label>
              </div>
              {!scopeSelection.tax && !scopeSelection.title && (
                <p className="scope-warning">⚠️ At least one product scope must be selected</p>
              )}
            </div>
            <div className="modal-footer">
              <Button 
                variant="outline"
                onClick={() => setScopeModalOpen(false)}
                disabled={scopeSaving}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveScope}
                disabled={scopeSaving || (!scopeSelection.tax && !scopeSelection.title)}
                isLoading={scopeSaving}
                data-testid="scope-save-btn"
              >
                {scopeSaving ? 'Saving...' : 'Save Scope'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
